import { faCalendarDays } from '@fortawesome/pro-light-svg-icons';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { range } from 'lodash';
import moment from 'moment';
import React, { InputHTMLAttributes, useMemo, useState } from 'react';
import { useOutsideClickHandler } from '../../../logic/otherUtilities/useOutsideClickHandler';
import VoxelIconButton from '../Buttons/VoxelIconButton';
import VoxelText from '../Text/VoxelText';

export interface VoxelDatePickerProps
  extends Pick<InputHTMLAttributes<HTMLInputElement>, 'disabled'> {
  id?: string;
  label?: string;
  labelPosition?: 'top' | 'left';
  size?: 'sm' | 'md';
  dateValue: string;
  setDateValue: (date: string) => void;
  activeDates?: string[];
  error?: boolean;
}

export interface PickerObject {
  date?: Date;
  weekday: string;
  weekdayNumber: number;
  day: number;
  disabled: boolean;
}

const VoxelDatePicker: React.FC<VoxelDatePickerProps> = ({
  label,
  labelPosition = 'left',
  disabled,
  id,
  size = 'sm',
  dateValue,
  setDateValue,
  activeDates,
  error,
}) => {
  let labelPositionCSS = '';
  if (labelPosition === 'left') {
    labelPositionCSS = 'flex-row flex-nowrap items-center gap-x-2';
  }
  if (labelPosition === 'top') {
    labelPositionCSS = 'flex-col gap-y-2';
  }
  const [showOptions, setShowOptions] = useState<boolean>(false);

  // Click outside form field
  const divRef = useOutsideClickHandler<HTMLDivElement>({
    action: () => {
      setShowOptions(false);
    },
    active: showOptions,
  });
  const weekdays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  // Displayed date is the first date in the displayed month
  const [displayedDate, setDisplayedDate] = useState<Date>(new Date(dateValue));
  var displayedDays = useMemo(() => {
    const year = moment(displayedDate).format('YYYY').toString();
    const month = moment(displayedDate).format('MMMM').toString();
    const nrDaysInMonth = moment(displayedDate).daysInMonth();
    const daysArray = range(1, nrDaysInMonth + 1).map((day): PickerObject => {
      const dayDate = new Date(
        Date.UTC(displayedDate.getUTCFullYear(), displayedDate.getUTCMonth(), day)
      );
      let isActive = true;
      if (!!activeDates?.length) {
        isActive = activeDates.some(
          (activeDate) =>
            moment.utc(activeDate).startOf('day').toDate().getTime() === dayDate.getTime()
        );
      }

      return {
        date: dayDate,
        day: day,
        weekday: weekdays[dayDate.getDay() - 1],
        weekdayNumber: dayDate.getDay(),
        disabled: !isActive,
      };
    });

    let emptyArray: number[] = [];
    if (daysArray[0].weekdayNumber > 0) {
      emptyArray = range(1, daysArray[0].weekdayNumber);
    }

    return {
      nrDaysInMonth,
      month,
      year,
      daysArray,
      emptyArray,
    };
  }, [displayedDate, activeDates]);

  const decreaseDisplayedMonth = () => {
    setDisplayedDate(new Date(displayedDate.setMonth(displayedDate.getMonth() - 1)));
  };
  const increaseDisplayedMonth = () => {
    setDisplayedDate(new Date(displayedDate.setMonth(displayedDate.getMonth() + 1)));
  };

  return (
    <div className={`flex ${labelPositionCSS}`}>
      {label?.length && (
        <label htmlFor={id}>
          <VoxelText variant="form-caption" noWrap>
            {label}
          </VoxelText>
        </label>
      )}
      <div className="relative group" ref={divRef}>
        <input
          id={id}
          disabled={disabled}
          placeholder={'Select date'}
          value={moment(dateValue).format('MMM D YYYY').toString()}
          readOnly
          onClick={() => {
            setShowOptions((prev) => !prev);
            setDisplayedDate(new Date(dateValue));
          }}
          className={`
          w-32 pl-7 pr-2.5
          ${size === 'md' ? 'py-2' : 'py-1'}
          rounded-md
          outline-none select-none
          ${!disabled && 'cursor-pointer'}
          ${!error && 'ring-voxel-gray-300'}
          ${error && 'ring-red-300'}
          ring-1
          hover:ring-voxel-gray-400 hover:ring-1
          focus:ring-voxel-gray-600 focus:ring-2
          ${showOptions && 'ring-2 ring-voxel-gray-600'}
          appearance-none font-sans text-[13px] font-medium
          
          `}
        />
        <div className={`absolute left-2 ${size === 'md' ? 'top-1.5 ' : 'top-1'}`}>
          <FontAwesomeIcon icon={faCalendarDays} className="text-voxel-gray-500" />
        </div>
        {showOptions && (
          <div
            className={`
              absolute left-0 right-0 top-10 z-[1200] p-3
              bg-white rounded-md shadow-lg ring-1 ring-gray-100 cursor-pointer w-72
              max-h-[520px] 
              overflow-y-auto scroll
              select-none
              flex flex-col gap-2
            `}
          >
            <div className="flex flex-row justify-between items-center">
              <div>
                <VoxelIconButton
                  size="xs"
                  color="white"
                  icon={<FontAwesomeIcon icon={faChevronLeft} />}
                  onClick={decreaseDisplayedMonth}
                />
              </div>
              <div>
                <VoxelText variant="bread-medium" color="medium">
                  {' '}
                  {displayedDays.month} {displayedDays.year}
                </VoxelText>
              </div>
              <div>
                <VoxelIconButton
                  size="xs"
                  color="white"
                  icon={<FontAwesomeIcon icon={faChevronRight} />}
                  onClick={increaseDisplayedMonth}
                />
              </div>
            </div>
            <div className="grid grid-cols-7 auto-cols-max justify-items-stretch">
              {weekdays.map((weekd, index) => (
                <div className="justify-self-center" key={index}>
                  <VoxelText color="light">{weekd}</VoxelText>
                </div>
              ))}
              {displayedDays.emptyArray.map((t, index) => (
                <div className="justify-self-center" key={index}></div>
              ))}
              {displayedDays.daysArray.map((day, index) => (
                <div className="justify-self-center" key={day.date?.toString()}>
                  {!!day.date && (
                    <VoxelIconButton
                      size="sm"
                      color={moment.utc(dateValue).isSame(day.date) ? 'blue' : 'white'}
                      onClick={() => setDateValue(day.date.toUTCString())}
                      disabled={day.disabled}
                      textColorClass={day.disabled ? 'voxel-gray-100' : ''}
                    >
                      <VoxelText variant="bread-semibold">{day.day}</VoxelText>
                    </VoxelIconButton>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoxelDatePicker;
